/**
 * Toast — lightweight, dependency-free transient notifications.
 *
 * Mount <Toaster /> once at the app root (wrapping the routed tree)
 * so it provides the context AND renders the visible toast stack.
 * Anywhere inside the tree, call `useToast()` to push messages:
 *
 *   const toast = useToast();
 *   toast.success('Tenant moved');
 *   toast.error('Resend failed: ' + msg);
 *   toast.info('…');
 *
 * Visual: fixed bottom-right, up to ~3 stacked, .card surface with a
 * left border in `accent` / `warn` / `muted` matching the variant.
 * Success/info auto-dismiss after ~4 s; ERROR toasts persist until the
 * operator dismisses them (× / Escape) so the message can be read or
 * copied. The toast queue is FIFO, but new toasts stack from the bottom.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastEntry {
  id: number;
  variant: ToastVariant;
  message: string;
}

interface ToastApi {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 4_000;
// Cap simultaneous toasts so a flurry doesn't bury the page.
const MAX_VISIBLE = 4;

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast must be used inside <Toaster> — mount it at the app root',
    );
  }
  return ctx;
}

export function Toaster({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  // Stable id counter — refs survive re-renders without causing them.
  const nextIdRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = nextIdRef.current++;
      setToasts((cur) => {
        // Drop the oldest if we'd exceed the visible cap. Newer
        // toasts are more relevant; better to lose the stale one.
        const next = [...cur, { id, variant, message }];
        return next.length > MAX_VISIBLE
          ? next.slice(next.length - MAX_VISIBLE)
          : next;
      });
      // Errors persist until dismissed — the operator may need to read or copy
      // the message (e.g. a deploy failure). Success/info auto-dismiss.
      if (variant !== 'error') window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (msg) => push('success', msg),
      error: (msg) => push('error', msg),
      info: (msg) => push('info', msg),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastHost toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

const BORDER: Record<ToastVariant, string> = {
  success: 'border-l-accent',
  error: 'border-l-warn',
  info: 'border-l-muted',
};

const ICON: Record<ToastVariant, string> = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
};

const ICON_COLOR: Record<ToastVariant, string> = {
  success: 'text-accent',
  error: 'text-warn',
  info: 'text-muted',
};

function ToastHost({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: number) => void;
}) {
  // Keyboard escape: dismiss the oldest visible toast. Tiny QoL win
  // for the operator who just saw an error and wants it out of the
  // way without reaching for the mouse.
  useEffect(() => {
    if (toasts.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const first = toasts[0];
        if (first) onDismiss(first.id);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toasts, onDismiss]);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-toast flex flex-col gap-2 pointer-events-none"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto card border-l-4 ${BORDER[t.variant]} shadow-lg min-w-[280px] max-w-[420px] flex items-start gap-3 py-2.5 px-3 artomeui-toast-enter`}
          role={t.variant === 'error' ? 'alert' : 'status'}
        >
          <span
            className={`text-sm font-semibold leading-5 ${ICON_COLOR[t.variant]}`}
            aria-hidden="true"
          >
            {ICON[t.variant]}
          </span>
          <span className="text-sm text-fg flex-1 leading-5 break-words">
            {t.message}
          </span>
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            className="text-muted hover:text-fg leading-5 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-accent rounded"
            aria-label="Dismiss notification"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
